/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { Icon, formatMoney, Button, SecurityHelper } from 'sarsaparilla';

import { OrderConfirmationPageInventoryItem } from './OrderConfirmationPageInventoryItem';
import type { Reservation } from './OrderConfirmationPage.types';

interface OrderConfirmationPageConfirmationBoxProps {
    email: string;
    orderID: string;
    reservations: Reservation[];
    subtotal: string;
    tax: string;
    total: string;
    modification: boolean;
}

export function OrderConfirmationPageConfirmationBox({
    modification,
    email,
    orderID,
    subtotal,
    reservations,
    tax,
    total,
}: OrderConfirmationPageConfirmationBoxProps) {
    const confirmationTitle = modification
        ? "We've updated your order!"
        : 'Thank you for your order!';

    let continueShoppingUrl = '/';
    if (SecurityHelper.isWidgetSession()) {
        continueShoppingUrl = SecurityHelper.getWidgetHomeUrl() ?? '/';
    }

    return (
        <div className="rec-order-confirmation-layout">
            <h1>{confirmationTitle}</h1>
            <p className="lead">
                A confirmation email will be sent out shortly to <strong>{email}</strong>
            </p>
            <div className="rec-order-confirmation-callout">
                <div className="rec-order-confirmation-callout-body">
                    <div className="rec-order-confirmation-callout-title">
                        Order #: {orderID}
                    </div>
                    <p>
                        <a href="/account/reservations">View My Reservations</a>
                    </p>
                    <Button href={continueShoppingUrl}>Continue Shopping</Button>
                </div>
            </div>
            <div className="rec-order-confirmation-actions">
                <button type="button" className="rec-button-link" onClick={window.print}>
                    <Icon iconName="print" />
                </button>
            </div>
            <div className="rec-order-confirmation-details">
                <div className="rec-order-confirmation-details-header">
                    Reservation Details
                </div>
                <div className="rec-order-confirmation-details-body">
                    {reservations.map((reservation, index) => {
                        return (
                            <OrderConfirmationPageInventoryItem
                                key={index}
                                reservation={reservation}
                            />
                        );
                    })}
                    <div className="rec-order-confirmation-details-footer">
                        <div className="rec-order-confirmation-total">
                            <div className="rec-order-confirmation-total-row">
                                <div className="rec-order-confirmation-total-label">
                                    Subtotal:
                                </div>
                                <div className="rec-order-confirmation-total-value">
                                    {formatMoney(subtotal)}
                                </div>
                            </div>
                            <div className="rec-order-confirmation-total-row">
                                <div className="rec-order-confirmation-total-label">
                                    Tax:
                                </div>
                                <div className="rec-order-confirmation-total-value">
                                    {formatMoney(tax)}
                                </div>
                            </div>
                            <div className="rec-order-confirmation-total-row">
                                <div className="rec-order-confirmation-total-label">
                                    Total:
                                </div>
                                <div className="rec-order-confirmation-total-value">
                                    <strong>{formatMoney(total)}</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
